import { Box, Container } from '@mui/material'
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import LoadingSpinner from '@/components/LoadingSpinner'
import { useAppSelector } from '@/hooks/redux'
import { removeAllImages, setCanvasImage } from '@/store/appSlice'
import {
  clearSelectedCases,
  clearTrashSelectedCases,
  setStatusFilters,
  toggleSelectedRow,
} from '@/store/project'
import { getSessionId } from '@/utils'
import { getCasesApi } from '@/utils/appApi'

import ActionMenu from './ActionMenu'
import ProjectsHeader from './ProjectHeader'
import ProjectsTable from './ProjectTable'
import ProjectsToolbar from './ProjectToolbar'

export type ProjectStatus = 'active' | 'scheduled' | 'archive'

export interface Project {
  name: string
  status: ProjectStatus
  dentist: string
  shared_with: string[]
  createdAt: string
  lastModifiedAt: string
  access: string
  createdBy: string
  id: string
}

const Projects = () => {
  const dispatch = useDispatch()

  const [tabValue, setTabValue] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuRowIndex, setMenuRowIndex] = useState<number | null>(null)
  const [search, setSearch] = useState('')

  const selectedRows = useAppSelector((state) => state.project.selectedRows)
  const statusFilters = useAppSelector((state) => state.project.statusFilters)
  const isLoggingOut = useAppSelector((state) => state.app.isLoggingOut)

  const [offset, setOffset] = useState(0)
  const [orderingTab0, setOrderingTab0] = useState<string>()
  const [orderingTab1, setOrderingTab1] = useState<string>()
  const limit = 10

  const [recipeData, setRecipeData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    dispatch(removeAllImages())
    dispatch(setCanvasImage(null))
  }, [dispatch])

  useEffect(() => {
    setOffset(0)
  }, [search])

  const currentOrdering = tabValue === 0 ? orderingTab0 : orderingTab1

  useEffect(() => {
    setOffset(0)
  }, [tabValue, statusFilters, currentOrdering])

  const queryArgs = useMemo(() => {
    const finalOrdering =
      tabValue === 1 && !orderingTab1 ? '-shared_ts' : currentOrdering
    return {
      shared: tabValue === 1,
      limit,
      offset,
      status: statusFilters.join(','),
      ordering: finalOrdering,
      search: search.trim(),
    }
  }, [
    tabValue,
    limit,
    offset,
    statusFilters,
    orderingTab1,
    currentOrdering,
    search,
  ])

  const sessionId = getSessionId()
  const shouldFetch =
    !isLoggingOut && (tabValue === 0 || tabValue === 1) && !!sessionId

  const fetchRecipes = useCallback(
    async (isInitial = false) => {
      if (!shouldFetch) return
      if (isInitial) setIsLoading(true)
      setIsFetching(true)
      try {
        const data = await getCasesApi(queryArgs)
        setRecipeData(data)
      } catch (error) {
        console.error('Failed to fetch recipes:', error)
      } finally {
        setIsLoading(false)
        setIsFetching(false)
      }
    },
    [queryArgs, shouldFetch],
  )

  useEffect(() => {
    fetchRecipes(true)
  }, [queryArgs])

  const [projects, setProjects] = useState<Project[]>([])

  useEffect(() => {
    if (recipeData?.results) {
      setProjects(recipeData?.results)
    }
  }, [recipeData, tabValue, statusFilters, currentOrdering])

  const handleCheckboxToggle = (id: string, name: string) => {
    dispatch(toggleSelectedRow({ id, name }))
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setSearch('')
    setOffset(0)
  }
  const handleStatusChange = (index: number, newStatus: ProjectStatus) => {
    const updated = [...projects]
    updated[index].status = newStatus
    setProjects(updated)
  }

  const handleMenuOpen = (event: MouseEvent<HTMLElement>, index: number) => {
    setAnchorEl(event.currentTarget)
    setMenuRowIndex(index)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuRowIndex(null)
  }

  const handlePrev = () => {
    dispatch(clearSelectedCases())
    dispatch(clearTrashSelectedCases())
    setOffset((prev) => Math.max(prev - limit, 0))
  }

  const handleNext = () => {
    dispatch(clearSelectedCases())
    dispatch(clearTrashSelectedCases())
    setOffset((prev) => prev + limit)
  }

  return (
    <Box overflow="auto" flex={1} sx={{ my: '32px' }}>
      <Container maxWidth="lg">
        <ProjectsHeader refetchRecipe={fetchRecipes} />
        <ProjectsToolbar
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          selectedRows={selectedRows}
          refetch={fetchRecipes}
          setSearch={setSearch}
          search={search}
        />
        {isLoading || isFetching ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <LoadingSpinner />
          </Box>
        ) : (
          <ProjectsTable
            projects={projects}
            handleStatusChange={handleStatusChange}
            handleMenuOpen={handleMenuOpen}
            tabValue={tabValue}
            selectedRows={selectedRows}
            handleCheckboxToggle={handleCheckboxToggle}
            page={Math.floor(offset / limit)}
            rowsPerPage={limit}
            count={recipeData?.count || 0}
            handlePrev={handlePrev}
            handleNext={handleNext}
            statusFilters={statusFilters}
            setStatusFilters={(filters) => dispatch(setStatusFilters(filters))}
            ordering={currentOrdering}
            setOrdering={tabValue === 0 ? setOrderingTab0 : setOrderingTab1}
            refetch={fetchRecipes}
          />
        )}

        <ActionMenu
          anchorEl={anchorEl}
          handleMenuClose={handleMenuClose}
          caseData={menuRowIndex !== null ? projects[menuRowIndex] : null}
          refetch={fetchRecipes}
          activeTab={tabValue}
        />
      </Container>
    </Box>
  )
}

export default Projects
